import { create } from 'zustand';
import type { ClientMsg, LobbyView, ServerMsg, Theme } from '@betrayal/shared';
import type { Bridge } from './bridge.js';
import { getTheme } from './themes.js';

interface AppState {
  bridge: Bridge | null;
  lobby: LobbyView | null;
  you: string;
  theme: Theme;
  connection: 'connecting' | 'open' | 'closed';
  error: string | null;
  send(msg: ClientMsg): void;
  connect(bridge: Bridge): void;
  dismissError(): void;
}

export const useStore = create<AppState>((set, get) => {
  let ws: WebSocket | null = null;
  let retry = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  function open(bridge: Bridge) {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    ws = new WebSocket(`${proto}://${location.host}${bridge.basePath}/ws`);
    set({ connection: 'connecting' });

    ws.onopen = () => {
      retry = 0;
      set({ connection: 'open' });
      ws!.send(
        JSON.stringify({ t: 'JOIN', token: bridge.sessionToken, instanceId: bridge.instanceId }),
      );
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data as string) as ServerMsg;
      switch (msg.t) {
        case 'HELLO':
          set({ you: msg.you, lobby: msg.lobby, theme: getTheme(msg.lobby.config.themeId) });
          break;
        case 'LOBBY':
          set({ lobby: msg.lobby, theme: getTheme(msg.lobby.config.themeId) });
          break;
        case 'ERROR':
          set({ error: msg.message });
          break;
        case 'PONG':
          break;
      }
    };

    ws.onclose = (ev) => {
      set({ connection: 'closed' });
      if (ev.code === 4001) {
        set({ error: 'Session expired — reload to sign in again.' });
        return;
      }
      // Reconnect with backoff; the server holds our seat.
      retryTimer = setTimeout(() => open(bridge), Math.min(500 * 2 ** retry++, 8000));
    };
    ws.onerror = () => ws?.close();
  }

  return {
    bridge: null,
    lobby: null,
    you: '',
    theme: getTheme('fantasy'),
    connection: 'connecting',
    error: null,
    connect(bridge) {
      clearTimeout(retryTimer);
      set({ bridge });
      open(bridge);
    },
    send(msg) {
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
      else set({ error: 'Not connected yet — hang on.' });
      void get;
    },
    dismissError() {
      set({ error: null });
    },
  };
});
